/**
 * Spanish translations for Pokémon whose API names differ from their official ES names.
 * Keys are PokeAPI slugs; values are official Spanish display names.
 */
export const POKEMON_NAMES_ES = {
  // Paradox Pokémon de Paldea - Futuro
  'iron-bundle':   'Ferrosaco',
  'iron-hands':    'Ferropalmas',
  'iron-jugulis':  'Ferrocuello',
  'iron-moth':     'Ferropolilla',
  'iron-thorns':   'Ferropúas',
  'iron-treads':   'Ferrodada',
  'iron-valiant':  'Ferropaladín',
  'iron-leaves':   'Ferroverdor',
  'iron-boulder':  'Ferromole',
  'iron-crown':    'Ferrotesta',
  // Paradox Pokémon de Paldea - Pasado
  'roaring-moon':  'Bramaluna',
  'scream-tail':   'Colagrito',
  'flutter-mane':  'Melenaleteo',
  'slither-wing':  'Reptalada',
  'sandy-shocks':  'Pelarena',
  'great-tusk':    'Colmilargo',
  'brute-bonnet':  'Furioseta',
  'walking-wake':  'Ondulagua',
  'gouging-fire':  'Flamariete',
  // Nombres especiales
  'mr-mime':       'Mr. Mime',
  'mr-rime':       'Mr. Rime',
  'mime-jr':       'Mime Jr.',
  'wishiwashi-solo': 'Wishiwashi',
  'type-null':     'Código Cero',
  'jangmo-o':      'Jangmo-o',
  'hakamo-o':      'Hakamo-o',
  'kommo-o':       'Kommo-o',
  'tapu-koko':     'Tapu Koko',
  'tapu-lele':     'Tapu Lele',
  'tapu-bulu':     'Tapu Bulu',
  'tapu-fini':     'Tapu Fini',
  'wo-chien':      'Wo-Chien',
  'chien-pao':     'Chien-Pao',
  'ting-lu':       'Ting-Lu',
  'chi-yu':        'Chi-Yu',
  'eiscue-ice':    'Eiscue',
  'wormadam-plant':'Wormadam',
  'koraidon':      'Koraidon',
  'miraidon':      'Miraidon',
  'gimmighoul':    'Gimmighoul',
  'gholdengo':     'Gholdengo',
  // Formas con sufijo → nombre base
  'tornadus-incarnate':      'Tornadus',
  'thundurus-incarnate':     'Thundurus',
  'landorus-incarnate':      'Landorus',
  'enamorus-incarnate':      'Enamorus',
  'deoxys-normal':           'Deoxys',
  'shaymin-land':            'Shaymin',
  'giratina-altered':        'Giratina',
  'rotom-heat':              'Rotom',
  'rotom-wash':              'Rotom',
  'rotom-frost':             'Rotom',
  'rotom-fan':               'Rotom',
  'rotom-mow':               'Rotom',
  'aegislash-shield':        'Aegislash',
  'zygarde-50':              'Zygarde',
  'meowstic-male':           'Meowstic',
  'meowstic-female':         'Meowstic',
  'basculin-red-striped':    'Basculin',
  'basculin-blue-striped':   'Basculin',
  'darmanitan-standard':     'Darmanitan',
  'keldeo-ordinary':         'Keldeo',
  'mimikyu-disguised':       'Mimikyu',
  'toxtricity-amped':        'Toxtricity',
  'minior-red-meteor':       'Minior',
  'eiscue-noice':            'Eiscue',
  'indeedee-male':           'Indeedee',
  'indeedee-female':         'Indeedee',
  'morpeko-full-belly':      'Morpeko',
  'urshifu-single-strike':   'Urshifu',
  'basculegion-male':        'Basculegion',
  'basculegion-female':      'Basculegion',
  'oinkologne-male':         'Oinkologne',
  'oinkologne-female':       'Oinkologne',
  'tatsugiri-curly':         'Tatsugiri',
  'maushold-family-of-four': 'Maushold',
  'squawkabilly-green-plumage': 'Squawkabilly',
  'dudunsparce-two-segment': 'Dudunsparce',
  'palafin-zero':            'Palafin',
  'nidoran-f':               'Nidoran♀',
  'nidoran-m':               'Nidoran♂',
};

// Import comprehensive translations
import { ABILITIES_ES } from './translations/es/abilities';
import { ITEMS_ES } from './translations/es/items';
import { MOVES_ES } from './translations/es/moves';

/**
 * Spanish translations for official Pokémon Abilities.
 */
export const ABILITY_NAMES_ES = ABILITIES_ES;

/**
 * Spanish translations for official Pokémon Items.
 */
export const ITEM_NAMES_ES = ITEMS_ES;

/**
 * Spanish translations for official Pokémon Moves.
 */
export const MOVE_NAMES_ES = MOVES_ES;

/**
 * Returns the display name for a Pokémon given its PokeAPI slug and the active language.
 * Falls back to capitalising the slug and replacing hyphens with spaces.
 *
 * @param {string} apiName - PokeAPI slug, e.g. "iron-bundle"
 * @param {string} language - "es" | "en"
 * @returns {string}
 */
export function formatPokemonName(apiName, language = 'es') {
  if (!apiName) return '';
  if (language === 'es' && POKEMON_NAMES_ES[apiName]) {
    return POKEMON_NAMES_ES[apiName];
  }
  return apiName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function formatAbilityName(apiName, language = 'es') {
  if (!apiName) return '';
  const slug = apiName.toLowerCase();
  if (language === 'es' && ABILITY_NAMES_ES[slug]) {
    return ABILITY_NAMES_ES[slug];
  }
  return apiName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function formatItemName(apiName, language = 'es') {
  if (!apiName) return '';
  const slug = apiName.toLowerCase();
  if (language === 'es' && ITEM_NAMES_ES[slug]) {
    return ITEM_NAMES_ES[slug];
  }
  return apiName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function formatMoveName(apiName, language = 'es') {
  if (!apiName) return '';
  const slug = apiName.toLowerCase();
  if (language === 'es' && MOVE_NAMES_ES[slug]) {
    return MOVE_NAMES_ES[slug];
  }
  return apiName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export const TYPE_NAMES_ES = {
  normal: 'Normal', fire: 'Fuego', water: 'Agua', electric: 'Eléctrico',
  grass: 'Planta', ice: 'Hielo', fighting: 'Lucha', poison: 'Veneno',
  ground: 'Tierra', flying: 'Volador', psychic: 'Psíquico', bug: 'Bicho',
  rock: 'Roca', ghost: 'Fantasma', dragon: 'Dragón', dark: 'Siniestro',
  steel: 'Acero', fairy: 'Hada', stellar: 'Estelar'
};

export const TYPE_NAMES_EN = {
  normal: 'Normal', fire: 'Fire', water: 'Water', electric: 'Electric',
  grass: 'Grass', ice: 'Ice', fighting: 'Fighting', poison: 'Poison',
  ground: 'Ground', flying: 'Flying', psychic: 'Psychic', bug: 'Bug',
  rock: 'Rock', ghost: 'Ghost', dragon: 'Dragon', dark: 'Dark',
  steel: 'Steel', fairy: 'Fairy', stellar: 'Stellar'
};

export function formatTypeName(apiName, language = 'es') {
  if (!apiName) return '';
  const slug = apiName.toLowerCase();
  if (language === 'es') return TYPE_NAMES_ES[slug] || apiName.charAt(0).toUpperCase() + apiName.slice(1);
  return TYPE_NAMES_EN[slug] || apiName.charAt(0).toUpperCase() + apiName.slice(1);
}
